import { ButtonVariant, CoButton } from "components/CoButton";
import Popover from "components/Popover";
import useFilters from "features/FilterDrawer/hooks/useFilters";
import { useCurrentThread } from "features/Thread/hooks/useCurrentThread";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import Tag from "../Tag";
import { PreprintIcon } from "design_system";

interface PreprintTagProps {
  isCompact?: boolean;
}

function PreprintTag({ isCompact }: PreprintTagProps = {}) {
  const [pageLabels] = useLabels("tooltips.preprint");
  const { appliedFilters } = useFilters();

  const { thread, interactionId, addInteractionToThread } =
    useCurrentThread() ?? {};
  const { currentSearchMode } = useSearchMode();

  const handleExcludePreprints = () => {
    if (!thread) return;

    const interaction = thread.interactions.find(
      (interaction) => interaction.id === interactionId
    );
    if (!interaction) return;

    // Add a new interaction with the preprints filter applied
    addInteractionToThread?.({
      userMessage: interaction.user_message,
      filters: {
        ...interaction.results?.filters,
        excludePreprints: true,
      },
      searchMode: currentSearchMode,
      actionSource: MixpanelActionSource.ThreadInteractionResults,
    });
  };

  return (
    <Popover
      interactive
      appendToParent
      tooltipContent={
        <div className="flex flex-col gap-3">
          <p className="flex gap-3 items-center sm-bold">
            <PreprintIcon size="lg" />
            {pageLabels["tag"]}
          </p>
          <p className="sm-normal">{pageLabels["title"]}</p>
          <p className="tiny-normal text-fg-muted">{pageLabels["content"]}</p>
          <CoButton
            variant={ButtonVariant.Tertiary}
            icon="icon-list-filter-plus"
            label={pageLabels["exclude-preprints"]}
            disabled={appliedFilters.excludePreprints}
            onClick={handleExcludePreprints}
          />
        </div>
      }
    >
      {isCompact ? (
        <div className="p-1">
          <PreprintIcon size="sm" />
        </div>
      ) : (
        <Tag
          label={pageLabels["tag"]}
          icon={<PreprintIcon size="sm" />}
          data-testid="preprint"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
    </Popover>
  );
}

export default PreprintTag;
